<template>
    <div>
        <v-toolbar dense>
            <v-toolbar-title>Duration Calculation</v-toolbar-title>
        </v-toolbar>

        <v-container fluid>
            <v-form ref="searchForm" @submit.prevent="calculate">
                <v-row>
                    <v-col cols="12" md="4">
                        <v-text-field v-model="orderId" label="Order ID" placeholder="Enter Order ID" outlined
                            @input="clearOriginDestinationDate" type="number"></v-text-field>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <p class="mb-0">Or search by Origin, Destination, and Date:</p>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" md="4">
                        <v-combobox v-model="origin" :items="originOptions" :item-text="'Name'" :item-value="'id'"
                            label="Origin" placeholder="Select or type Origin" outlined
                            @input="clearOrderId"></v-combobox>
                    </v-col>
                    <v-col cols="12" md="4">

                        <v-combobox v-model="destination" :items="destinationOptions" :item-text="'Name'"
                            :item-value="'id'" label="Destination" placeholder="Select or type Destination" outlined
                            @input="clearOrderId"></v-combobox>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-menu ref="datePickerMenu" v-model="menu" :close-on-content-click="false"
                            transition="scale-transition" offset-y min-width="290px">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field v-model="date" label="Date" readonly v-bind="attrs" v-on="on" outlined
                                    @input="clearOrderId"></v-text-field>
                            </template>
                            <v-date-picker v-model="date" no-title @input="menu = false"></v-date-picker>
                        </v-menu>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-btn color="primary" @click="calculate">Calculate</v-btn>
                    </v-col>
                </v-row>
            </v-form>

            <v-snackbar v-model="snackbar.visible" :color="snackbar.color" timeout="3000">
                {{ snackbar.message }}
                <template v-slot:action="{ attrs }">
                    <v-btn text v-bind="attrs" @click="snackbar.visible = false">Close</v-btn>
                </template>
            </v-snackbar>
        </v-container>

        <template v-if="calculationData.length > 0">
            <DurationCalculationResult :calculationData="calculationData" :calculationMetaData="calculationMetaData" />
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import DurationCalculationResult from './DurationCalculationResult.vue';

export default {
    components: {
        DurationCalculationResult,
    },
    data() {
        return {
            orderId: "",
            origin: "",
            destination: "",
            date: "",
            menu: false,
            originOptions: [],
            destinationOptions: [],
            isLoading: false,
            snackbar: {
                visible: false,
                message: "",
                color: "info",
            },
            calculationData: [],
            calculationMetaData: {},
        };
    },
    mounted() {
        this.loadOrigin();
        this.loadDestination();
    },
    methods: {
        async calculate() {
            if (this.isLoading) return;

            if (this.orderId && (this.origin || this.destination || this.date)) {
                this.showSnackbar(
                    "You can only enter either Order ID or Origin, Destination, and Date, not both.",
                    "error"
                );
                return;
            }

            if (!this.orderId && (!this.origin || !this.destination || !this.date)) {
                this.showSnackbar(
                    "Please provide either an Order ID or complete Origin, Destination, and Date.",
                    "error"
                );
                return;
            }

            this.isLoading = true;

            const payload = {
                OrderID: this.orderId || null,
                Origin: this.origin.id || null,
                Destination: this.destination.id || null,
                Date: this.formatDate(this.date),
            };

            try {
                const response = await axios.post(
                    "/dispatch-plan-b/find-avg-time/",
                    payload
                );
                this.calculationData = [];
                const data = response.data.data;
                if (data) {
                    for (const [key, value] of Object.entries(data)) {
                        const calcData = {
                            event: key,
                            zoneOriginId: value && value.FromZone ? value.FromZone : "N/A",
                            zoneDestinationId: value && value.ToZone ? value.ToZone : "N/A",
                            duration: value && value.AverageTime ? Number(value.AverageTime).toFixed(2) : "N/A",
                        }
                        this.calculationData.push(calcData);
                    }
                }


                if (response.data.meta_info) {
                    let meta = response.data.meta_info;
                    meta.origin = this.origin.Name;
                    meta.destination = this.destination.Name;
                    this.calculationMetaData = meta;
                }

                this.showSnackbar("Calculation successful.", "success");
            } catch (error) {
                console.error("Error during API call:", error);
                this.showSnackbar(
                    "An error occurred while calculating. Please try again.",
                    "error"
                );
            } finally {
                this.isLoading = false;
            }
        },
        loadOrigin: async function () {
            this.$store.state.isLoading = true;
            try {
                const response = await axios.get('terminal/active');
                this.originOptions = response.data.data;
                if (!this.originOptions.length) {
                    this.showSnackbar("No origin options available.", "warning");
                }
            } catch (error) {
                this.showSnackbar("Error loading origin data.", "error");
            } finally {
                this.$store.state.isLoading = false;
            }
        },

        loadDestination: async function () {
            this.$store.state.isLoading = true;
            try {
                const response = await axios.get('station/active');
                this.destinationOptions = response.data.data;
                if (!this.destinationOptions.length) {
                    this.showSnackbar("No destination options available.", "warning");
                }
            } catch (error) {
                this.showSnackbar("Error loading destination data.", "error");
            } finally {
                this.$store.state.isLoading = false;
            }
        },
        formatDate(date) {
            if (!date) return null;
            return moment(date).format('YYYY-MM-DD');
        },

        showSnackbar(message, type) {
            const colors = {
                success: "green",
                error: "red",
                warning: "orange",
                info: "blue",
            };
            this.snackbar.message = message;
            this.snackbar.color = colors[type] || "info";
            this.snackbar.visible = true;
        },
        clearOrderId() {
            this.orderId = "";
        },

        clearOriginDestinationDate() {
            this.origin = "";
            this.destination = "";
            this.date = "";
        },
    },
};
</script>